import { useCallback, useMemo, useState } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { Invoice, InvoiceStatus } from "@common/types/invoice";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import {
    useBulkUpdateInvoiceCounterparty,
    useBulkUpdateInvoiceStatus,
} from "@api/invoice.api";
import { useLockListState } from "@api/lock.api";
import { getLatestLockDate, laterDateOnly } from "@helpers/lock";

interface UseInvoiceBulkActionsParams {
    selectedInvoices: Invoice[];
    onActionComplete?: () => void;
}

// Reused for both bulk lifecycle transitions this hook drives: Mark Void
// and Mark Uncollectible. `null` means no modal is open.
type PendingBulkStatusAction =
    | { kind: "void" }
    | { kind: "uncollectible" }
    | null;

// Shared by every bulk lifecycle transition (Mark Void, Mark Uncollectible):
// each invoice is applied independently server-side, so a selection spanning
// eligible and ineligible invoices doesn't fail wholesale — it reports which
// ones succeeded and which were skipped with a reason.
function summarizeBulkStatusResult({
    label,
    succeeded,
    failed,
}: {
    label: string;
    succeeded: string[];
    failed: Array<{ invoiceId: string; message: string }>;
}): { message: string; variant: "success" | "warning" | "error" } {
    if (failed.length === 0) {
        return {
            message: `${pluralizeWithCount("invoice", succeeded.length)} marked ${label}`,
            variant: "success",
        };
    }
    if (succeeded.length === 0) {
        return {
            message: `Couldn't mark any invoices ${label}: ${failed[0].message}`,
            variant: "error",
        };
    }
    return {
        message: `Marked ${pluralizeWithCount("invoice", succeeded.length)} ${label}. ${failed.length} skipped: ${failed[0].message}`,
        variant: "warning",
    };
}

export function useInvoiceBulkActions({
    selectedInvoices,
    onActionComplete,
}: UseInvoiceBulkActionsParams) {
    const { workspaceId } = useScope();
    const { toast } = useToaster();
    const bulkUpdateStatus = useBulkUpdateInvoiceStatus();
    const bulkUpdateCounterparty = useBulkUpdateInvoiceCounterparty();
    const [pendingStatusAction, setPendingStatusAction] =
        useState<PendingBulkStatusAction>(null);

    // The bulk void/uncollectible date picker must disable dates in a locked
    // period for EVERY selected invoice's entity, not just one of them — the
    // latest lock date across the set is the tightest floor that satisfies
    // all of them (see `getLatestLockDate`).
    const selectedEntityIds = useMemo(
        () => [...new Set(selectedInvoices.map((invoice) => invoice.entityId))],
        [selectedInvoices],
    );
    const { locks: selectedEntityLocks, isResolved: isBulkStatusLockResolved } =
        useLockListState(selectedEntityIds);
    const bulkStatusLockDate = useMemo(
        () => getLatestLockDate(selectedEntityLocks),
        [selectedEntityLocks],
    );

    // The bulk void/uncollectible date picker must also respect the
    // per-invoice invoice-date floor (voidedAt/uncollectableAt must be on or
    // after invoiceDate — see assertDatesOnOrAfterInvoiceDate). invoiceDate is
    // only set for native (MANUAL source) invoices; every other source has
    // invoiceDate: null and doesn't constrain the floor. The latest invoice
    // date across the set is the tightest floor that satisfies all of them,
    // same reasoning as bulkStatusLockDate above.
    const bulkStatusMinInvoiceDate = useMemo(
        () =>
            selectedInvoices.reduce<string | undefined>(
                (latest, invoice) =>
                    laterDateOnly(latest, invoice.invoiceDate ?? undefined),
                undefined,
            ),
        [selectedInvoices],
    );

    // Change Counterparty is only offered when every selected invoice is a
    // Draft — the backend rejects the whole batch otherwise (no partial
    // application), so gate the entry point the same way rather than let the
    // user hit a 400 after picking a counterparty.
    const canBulkChangeCounterparty = useMemo(
        () =>
            selectedInvoices.length > 0 &&
            selectedInvoices.every(
                (invoice) => invoice.status === InvoiceStatus.DRAFT,
            ),
        [selectedInvoices],
    );

    const openBulkVoidModal = useCallback(() => {
        setPendingStatusAction({ kind: "void" });
    }, []);

    const openBulkUncollectibleModal = useCallback(() => {
        setPendingStatusAction({ kind: "uncollectible" });
    }, []);

    const closeBulkStatusModal = useCallback(() => {
        setPendingStatusAction(null);
    }, []);

    const confirmBulkChangeCounterparty = useCallback(
        async (counterpartyId: string) => {
            if (!workspaceId || selectedInvoices.length === 0) {
                return;
            }
            try {
                const result = await bulkUpdateCounterparty.mutateAsync({
                    workspaceId,
                    invoiceIds: selectedInvoices.map((invoice) => invoice.id),
                    counterpartyId,
                });
                if (result.failed.length === 0) {
                    toast(
                        `Updated counterparty for ${pluralizeWithCount("invoice", result.succeeded.length)}`,
                        undefined,
                        { variant: "success" },
                    );
                } else {
                    toast(
                        `Couldn't update counterparty for ${result.failed.length} of ${selectedInvoices.length} invoices: ${result.failed[0].message}`,
                        undefined,
                        { variant: "error" },
                    );
                }
                onActionComplete?.();
            } catch (error) {
                toast(
                    error instanceof Error && error.message
                        ? `Couldn't update counterparty: ${error.message}`
                        : "Couldn't update counterparty",
                    undefined,
                    { variant: "error" },
                );
            }
        },
        [
            workspaceId,
            selectedInvoices,
            bulkUpdateCounterparty,
            toast,
            onActionComplete,
        ],
    );

    const confirmBulkStatusDate = useCallback(
        async (date: string) => {
            if (
                !workspaceId ||
                !pendingStatusAction ||
                selectedInvoices.length === 0
            ) {
                setPendingStatusAction(null);
                return;
            }
            const status =
                pendingStatusAction.kind === "void"
                    ? InvoiceStatus.VOID
                    : InvoiceStatus.UNCOLLECTABLE;
            const label =
                pendingStatusAction.kind === "void" ? "void" : "uncollectible";
            try {
                const result = await bulkUpdateStatus.mutateAsync({
                    workspaceId,
                    invoiceIds: selectedInvoices.map((invoice) => invoice.id),
                    status,
                    date,
                });
                const { message, variant } = summarizeBulkStatusResult({
                    label,
                    succeeded: result.succeeded,
                    failed: result.failed,
                });
                toast(message, undefined, { variant });
                onActionComplete?.();
            } catch {
                toast(`Couldn't mark invoices ${label}`, undefined, {
                    variant: "error",
                });
            } finally {
                setPendingStatusAction(null);
            }
        },
        [
            workspaceId,
            pendingStatusAction,
            selectedInvoices,
            bulkUpdateStatus,
            toast,
            onActionComplete,
        ],
    );

    return {
        openBulkVoidModal,
        openBulkUncollectibleModal,
        closeBulkStatusModal,
        confirmBulkStatusDate,
        pendingStatusAction,
        bulkStatusLockDate,
        bulkStatusMinInvoiceDate,
        isBulkStatusLockResolved,
        isProcessingBulkStatus: bulkUpdateStatus.isPending,
        canBulkChangeCounterparty,
        confirmBulkChangeCounterparty,
        isProcessingBulkChangeCounterparty: bulkUpdateCounterparty.isPending,
    };
}
